// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useCallback, useEffect } from 'react';

import { Tabs } from '@mantine/core';
import { useParams } from 'react-router';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';

import { LinkModelsPane } from './LinkProjectPane';
import { ProjectPropertiesForm, type ProjectPropertiesFormProps } from './ProjectPropertiesForm';

const projectPropertiesTabs = ['info', 'members', 'models'] as const;
export type ProjectPropertiesTab = (typeof projectPropertiesTabs)[number];

export const ProjectDetailsPane = (props: Omit<ProjectPropertiesFormProps, 'allProjects'>) => {
  const navigate = useEvolveNavigate('Projects', true);

  // Store the selected tab on the URL,
  // so that if the user is redirected to Autodesk to sign in,
  // they will be sent back to the proper tab afterwords.
  const { tab } = useParams();
  const onTabSelected = useCallback(
    (newTab: ProjectPropertiesTab) => {
      void navigate(`/projects/${newTab}`, {
        replace: true,
      });
    },
    [navigate],
  );

  useEffect(() => {
    if (!projectPropertiesTabs.includes(tab as ProjectPropertiesTab)) {
      onTabSelected(projectPropertiesTabs[0]);
    }
  }, [onTabSelected, tab]);

  const selectedTab = tab as ProjectPropertiesTab;

  return (
    <Tabs
      value={selectedTab}
      variant="outline"
      onChange={(v) => onTabSelected(v as ProjectPropertiesTab)}
      keepMounted={false}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <Tabs.List mb="md">
        <Tabs.Tab leftSection={<EvolveIcon icon="Project" light={selectedTab !== 'info'} />} value="info">
          Properties
        </Tabs.Tab>
        <Tabs.Tab
          disabled={props.editing}
          leftSection={<EvolveIcon icon="ModelBrowser" light={selectedTab !== 'models'} />}
          value="models"
        >
          ACC/BIM 360
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="info" style={{ overflow: 'hidden' }}>
        <ProjectPropertiesForm {...props} />
      </Tabs.Panel>
      <Tabs.Panel
        value="models"
        styles={{ panel: { flexDirection: 'column', display: 'flex', flex: 1, overflow: 'hidden' } }}
      >
        <LinkModelsPane
          projectId={props.selectedProject.projectId}
          refreshProjects={() => props.onProjectUpdated(props.selectedProject)}
        />
      </Tabs.Panel>
    </Tabs>
  );
};
